import React, { useState, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ProductCard } from '../components/ProductCard';
import { projects } from '../data/projects';
import { getProjectCategories, filterProjectsByCategory } from '../utils/projectFilters';
import { SEO, breadcrumbs } from '../components/SEO';

export const Portfolio: React.FC = () => {
  const { t } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = useMemo(() => getProjectCategories(projects), []);
  const filteredProjects = useMemo(
    () => filterProjectsByCategory(projects, selectedCategory),
    [selectedCategory]
  );

  return (
    <div className="pt-24 pb-20">
      <SEO 
        title="Free Tools"
        description="Free browser extensions and tools for developers and e-commerce sellers. Built by Hüseyin Cem Ateş."
        canonical="/free-tools"
        jsonLd={breadcrumbs.portfolio}
      />
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-semibold mb-4">
            {t('portfolio', 'title')}
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t('portfolio', 'subtitle')}
          </p>
        </div>

        {/* Category Filters */}
        <div className="mb-12">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map(({ category, count }) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  selectedCategory === category
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('portfolio', `categories.${category}`)} ({count})
              </button>
            ))}
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <ProductCard key={project.id} product={project} />
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              {t('portfolio', 'noProjects')}
            </p>
          </div>
        )}

        {/* Hire Me CTA Section */}
        <div className="mt-20 pt-12 border-t border-border">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold mb-3">Need Something Custom?</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              These tools are just a taste of what I can build. Let's discuss your project and create something tailored to your needs.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/consultation"
              className="inline-flex items-center justify-center px-6 py-3 bg-foreground text-background rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              Book a Consultation
            </a>
            <a
              href="/about"
              className="inline-flex items-center justify-center px-6 py-3 border border-border rounded-lg font-medium hover:bg-muted transition-colors"
            >
              Learn About Me
            </a>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="mt-16 p-8 bg-muted/50 rounded-xl text-center">
          <h3 className="text-xl font-semibold mb-2">Stay Updated</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Get notified when I release new free tools, guides, and resources.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-foreground/20"
            />
            <button className="px-6 py-2.5 bg-foreground text-background rounded-lg font-medium hover:opacity-90 transition-opacity whitespace-nowrap">
              Get Updates
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
